import { Link } from "react-router-dom";

const MAIN_IMG = "/about/terrazzo-slabs.jpg";
const ACCENT_IMG = "/home/bricks-card.jpg";

const PRINCIPLES = [
  {
    n: "01",
    title: "Uncompromising Quality",
    body:
      "Every tile, every slab, every surface passes through rigorous quality controls. We source only the finest raw materials and employ master craftsmen with decades of experience.",
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.4" className="w-7 h-7">
        <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
      </svg>
    ),
  },
  {
    n: "02",
    title: "Design Innovation",
    body:
      "Our in-house design team constantly explores new patterns, textures, and colour palettes. We don't follow trends — we craft the surfaces that inspire them.",
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.4" className="w-7 h-7">
        <path d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
      </svg>
    ),
  },
  {
    n: "03",
    title: "Sustainable Craft",
    body:
      "We are committed to responsible manufacturing — minimising waste, using recycled aggregates where possible, and building surfaces that last a lifetime.",
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.4" className="w-7 h-7">
        <path d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064" />
      </svg>
    ),
  },
];

const STATS = [
  { num: "500+", label: "Projects Delivered" },
  { num: "200+", label: "Design Patterns" },
  { num: "18+", label: "States Covered" },
  { num: "15+", label: "Years of Excellence" },
];

export default function About() {
  return (
    <div data-testid="about-page" className="bg-cream">
      {/* ===== Page heading ===== */}
      <section data-testid="about-heading" className="px-4 sm:px-6 md:px-16 lg:px-20 pt-14 sm:pt-20 md:pt-28 pb-10 sm:pb-16">
        <div className="max-w-[1600px] mx-auto grid grid-cols-12 gap-y-6 gap-x-0 sm:gap-x-6 sm:gap-y-8">
          <div className="col-span-12 md:col-span-4">
            <p className="font-mono-label text-terracotta">About</p>
          </div>
          <div className="col-span-12 md:col-span-8">
            <h1 className="font-serif-display text-4xl sm:text-5xl md:text-7xl lg:text-8xl leading-[0.98]">
              Our <em className="italic text-terracotta">Story</em>.
            </h1>
          </div>
        </div>
      </section>

      {/* ===== Who We Are ===== */}
      <section data-testid="about-who" className="px-4 sm:px-6 md:px-16 lg:px-20 pt-4 sm:pt-6 pb-12 md:pb-20">
        <div className="max-w-[1600px] mx-auto grid grid-cols-12 gap-y-10 gap-x-0 lg:gap-x-16 items-center">
          {/* Text */}
          <div className="col-span-12 lg:col-span-6 order-2 lg:order-1">
            <p className="font-mono-label text-terracotta mb-5 sm:mb-6">— Who We Are</p>
            <h2 className="font-serif-display text-3xl sm:text-4xl md:text-6xl leading-[1.0]">
              Built on <em className="italic text-terracotta">Craft</em>,<br />
              Driven by Design.
            </h2>
            <div className="w-14 sm:w-16 h-px bg-ink/30 my-6 sm:my-8" />
            <div className="space-y-4 sm:space-y-5 text-stone-text leading-relaxed text-sm sm:text-base md:text-lg max-w-2xl">
              <p>
                Yogii Industries was founded on a singular belief: that surfaces are not mere
                construction materials — they are the first language of a space. They speak before
                furniture, before lighting, before decoration.
              </p>
              <p>
                Headquartered in India, we operate two specialist manufacturing brands —
                <span className="text-ink font-medium"> Terrazzo India</span> and
                <span className="text-ink font-medium"> Cement Bricks</span> — each with dedicated
                production facilities, quality labs, and design studios.
              </p>
              <p>
                Our clients span five-star hospitality groups, luxury residential developers,
                national retail chains, and globally renowned interior design studios. Every
                surface we produce is built to outlast trends and outlive generations.
              </p>
            </div>
          </div>

          {/* Image collage with badge */}
          <div className="col-span-12 lg:col-span-6 order-1 lg:order-2 relative px-4 sm:px-0">
            <div className="image-zoom-wrap aspect-[5/6] max-w-[420px] sm:max-w-[480px] lg:max-w-[540px] mx-auto lg:ml-auto lg:mr-0">
              <img src={MAIN_IMG} alt="Terrazzo casting" className="w-full h-full object-cover" />
            </div>
            <div className="absolute -bottom-8 -left-2 md:-left-10 w-[40%] sm:w-[42%] aspect-square image-zoom-wrap border-[5px] sm:border-[6px] border-cream shadow-2xl hidden sm:block">
              <img src={ACCENT_IMG} alt="Cement cladding bricks" className="w-full h-full object-cover" />
            </div>
            <div
              data-testid="about-badge"
              className="absolute top-4 sm:top-6 left-2 sm:-left-2 md:left-0 w-24 h-24 sm:w-28 sm:h-28 md:w-36 md:h-36 rounded-full flex flex-col items-center justify-center text-cream shadow-2xl z-10"
              style={{ background: "var(--terracotta)" }}
            >
              <span className="font-serif-display text-3xl sm:text-4xl md:text-5xl leading-none">2</span>
              <span className="font-mono-label mt-1.5 sm:mt-2 text-center px-2 text-[0.55rem] sm:text-xs" style={{color: "rgba(243,236,224,0.9)"}}>Iconic<br/>Brands</span>
            </div>
          </div>
        </div>
      </section>

      {/* ===== Our Principles ===== */}
      <section data-testid="about-principles" className="bg-stone py-12 sm:py-16 md:py-20 px-4 sm:px-6 md:px-16 lg:px-20 mt-12 sm:mt-0">
        <div className="max-w-[1600px] mx-auto">
          <div className="text-center mb-8 sm:mb-10 md:mb-14">
            <p className="font-mono-label text-terracotta mb-3">— Our Principles</p>
            <h2 className="font-serif-display text-3xl sm:text-4xl md:text-6xl">
              What We <em className="italic text-terracotta">Stand For</em>
            </h2>
            <div className="w-10 sm:w-12 h-px bg-ink/30 mx-auto mt-5 sm:mt-6" />
          </div>

          <div className="grid md:grid-cols-3 gap-5 sm:gap-6 md:gap-8">
            {PRINCIPLES.map((p) => (
              <div
                key={p.n}
                data-testid={`principle-${p.n}`}
                className="group bg-cream p-7 sm:p-10 md:p-12 border border-warm/60 hover:border-terracotta transition-colors relative overflow-hidden"
              >
                <div className="flex items-center justify-between mb-7 sm:mb-10">
                  <div className="w-12 h-12 sm:w-14 sm:h-14 flex items-center justify-center text-terracotta border border-terracotta/40 rounded-full group-hover:bg-terracotta group-hover:text-cream transition-colors">
                    {p.icon}
                  </div>
                  <span className="font-mono-label text-stone-text">{p.n}</span>
                </div>
                <h3 className="font-serif-display text-xl sm:text-2xl md:text-3xl mb-4 sm:mb-5 leading-tight">
                  {p.title}
                </h3>
                <p className="text-stone-text leading-relaxed text-sm md:text-base">{p.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== Two Specialist Brands ===== */}
      <section data-testid="about-brands" className="px-4 sm:px-6 md:px-16 lg:px-20 py-12 sm:py-16 md:py-20">
        <div className="max-w-[1600px] mx-auto">
          <div className="grid grid-cols-12 gap-y-6 gap-x-0 sm:gap-x-6 sm:gap-y-8 mb-8 sm:mb-12">
            <div className="col-span-12 md:col-span-4">
              <p className="font-mono-label text-terracotta">— Our Brands</p>
            </div>
            <div className="col-span-12 md:col-span-8">
              <h2 className="font-serif-display text-3xl sm:text-4xl md:text-6xl leading-[1.05]">
                Two Specialist <em className="italic text-terracotta">Brands</em>.
              </h2>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-6 md:gap-8">
            {/* Terrazzo India */}
            <div data-testid="brand-strip-terrazzo" className="bg-ink text-cream p-7 sm:p-10 md:p-14 relative overflow-hidden group">
              <div className="absolute -top-12 -right-12 w-56 h-56 rounded-full opacity-10 group-hover:opacity-20 transition-opacity" style={{background: "var(--terracotta)"}} />
              <p className="font-mono-label mb-5 sm:mb-6" style={{color: "rgba(243,236,224,0.55)"}}>
                Terrazzo Manufacturing
              </p>
              <h3 className="font-serif-display text-3xl sm:text-4xl md:text-5xl mb-5 sm:mb-6">
                Terrazzo <em className="italic" style={{color: "#c8ac7b"}}>India</em>
              </h3>
              <p className="text-sm sm:text-base leading-relaxed mb-8 sm:mb-10 max-w-md" style={{color: "rgba(243,236,224,0.78)"}}>
                India's premium terrazzo tile manufacturer. We create custom terrazzo surfaces with
                marble chips, glass, brass, copper, and granite aggregates in endless colour
                combinations. From Venetian terrazzo to contemporary epoxy-bound designs, Terrazzo
                India brings the ancient craft into modern architecture.
              </p>
              <Link
                to="/products"
                data-testid="brand-strip-terrazzo-cta"
                className="inline-flex items-center font-mono-label px-6 sm:px-7 py-3.5 sm:py-4 bg-terracotta hover:bg-cream hover:text-ink transition-colors"
                style={{color: "#f3ece0"}}
              >
                Explore Terrazzo India →
              </Link>
            </div>

            {/* Cement Bricks */}
            <div data-testid="brand-strip-mattoni" className="bg-cream-2 p-7 sm:p-10 md:p-14 border border-warm relative overflow-hidden group">
              <div className="absolute -top-12 -right-12 w-56 h-56 rounded-full opacity-20 group-hover:opacity-30 transition-opacity" style={{background: "var(--terracotta)"}} />
              <p className="font-mono-label text-stone-text mb-5 sm:mb-6">
                Cement Cladding Manufacturing
              </p>
              <h3 className="font-serif-display text-3xl sm:text-4xl md:text-5xl mb-5 sm:mb-6">
                Cement <em className="italic text-terracotta">Bricks</em>
              </h3>
              <p className="text-sm sm:text-base leading-relaxed mb-8 sm:mb-10 max-w-md text-stone-text">
                Artisanal cement cladding brick specialists. We produce handcrafted cladding bricks
                in architectural greys, naturals, and bespoke pigment runs. Built for floors, walls,
                and facades — each brick individually pressed, pigmented and cured by skilled artisans.
              </p>
              <Link
                to="/bricks"
                data-testid="brand-strip-mattoni-cta"
                className="inline-flex items-center font-mono-label px-6 sm:px-7 py-3.5 sm:py-4 bg-ink text-cream hover:bg-terracotta transition-colors"
              >
                Explore Cement Bricks →
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ===== Stats Band ===== */}
      <section data-testid="about-stats" className="bg-ink text-cream py-10 sm:py-14 md:py-18">
        <div className="max-w-[1600px] mx-auto px-4 sm:px-6 md:px-16 lg:px-20">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-y-10 sm:gap-y-12">
            {STATS.map((s, i) => (
              <div
                key={s.label}
                data-testid={`stat-${i}`}
                className={`text-center px-2 sm:px-4 ${i < STATS.length - 1 ? "md:border-r border-cream/15" : ""}`}
              >
                <p className="font-serif-display text-4xl sm:text-5xl md:text-6xl lg:text-7xl" style={{color: "#c8ac7b"}}>
                  {s.num}
                </p>
                <p className="font-mono-label mt-3 sm:mt-4 text-[0.6rem] sm:text-xs" style={{color: "rgba(243,236,224,0.65)"}}>
                  {s.label}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== Final CTA ===== */}
      <section className="px-4 sm:px-6 md:px-16 lg:px-20 py-12 sm:py-16 md:py-20 bg-cream-2">
        <div className="max-w-[1200px] mx-auto text-center">
          <p className="font-mono-label text-terracotta mb-3 sm:mb-4">— Let's begin</p>
          <h2 className="font-serif-display text-3xl sm:text-4xl md:text-6xl leading-[1.05]">
            Start with a <em className="italic text-terracotta">sample</em>.
          </h2>
          <p className="mt-5 sm:mt-6 text-sm sm:text-base text-stone-text max-w-xl mx-auto leading-relaxed">
            Send a reference photo, a drawing, or just a colour. We'll cast a sample and ship it to your studio.
          </p>
          <Link
            to="/request-quote"
            data-testid="about-cta"
            className="inline-flex mt-8 sm:mt-10 font-mono-label px-7 sm:px-8 py-3.5 sm:py-4 bg-ink text-cream hover:bg-terracotta transition-colors"
          >
            Request a sample →
          </Link>
        </div>
      </section>
    </div>
  );
}
